import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { isNotNilOrEmpty } from 'utils/ramda'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import { Skeleton } from '@mui/material'

const getFilesCount = (node) => node?.files_count ?? node?.filesCount ?? 0
const getGrantedFilesCount = (node) => node?.granted_files_count ?? node?.grantedFilesCount ?? 0
const getEditableFilesCount = (node) => node?.editable_files_count ?? node?.editableFilesCount ?? 0
const isFileEditable = (file) => (file?.is_access_editable ?? file?.isAccessEditable ?? true) === true
const isFileGranted = (file) => (file?.is_access_granted ?? file?.isAccessGranted ?? false) === true

const FileTreeSelector = ({
  fileTree = [],
  selected = [],
  getSelectedSnapshot = null,
  onSelectionChange,
  onSelectionIntent = null,
  loading = false,
  loadingCategoryIds = [],
  onCategoryExpand = null,
  maxHeight,
  compact = false,
  pendingSelectAll = false,
  onPendingSelectAllDeactivate = null,
  dirtyCategoryIds = [],
  selectionBaseline = 'server'
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [shouldCollapse, setShouldCollapse] = useState(false)
  const [openAccordions, setOpenAccordions] = useState({})
  const searchInputRef = useRef(null)
  const hasAutoFocusedRef = useRef(false)

  useEffect(() => {
    if (!loading && fileTree.length > 0) {
      const totalFiles = fileTree.reduce((acc, cat) => {
        const types = Object.values(cat.fileTypes || cat.file_types || {})

        if (cat.is_loaded === false) {
          return acc + types.reduce((count, t) => count + getFilesCount(t), 0)
        }

        return acc + types.reduce((count, t) => count + (t.files?.length || 0), 0)
      }, 0)

      const collapse = totalFiles > 3
      setShouldCollapse(collapse)

      if (collapse) {
        setOpenAccordions(prev => {
          const nextState = { ...prev }

          fileTree.forEach(cat => {
            if (typeof nextState[cat.id] === 'undefined') {
              nextState[cat.id] = false
            }
          })

          return nextState
        })
      } else {
        setOpenAccordions({})
      }
    }
  }, [fileTree, loading])

  useEffect(() => {
    if (!loading && fileTree.length > 0 && !hasAutoFocusedRef.current) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
        hasAutoFocusedRef.current = true
      })
    }
  }, [loading, fileTree])

  useEffect(() => {
    if (loading || fileTree.length === 0) {
      hasAutoFocusedRef.current = false
    }
  }, [loading, fileTree.length])

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const dirtyCategorySet = useMemo(() => new Set(dirtyCategoryIds), [dirtyCategoryIds])
  const loadingCategorySet = useMemo(() => new Set(loadingCategoryIds), [loadingCategoryIds])
  const getLatestSelected = useCallback(
    () => (typeof getSelectedSnapshot === 'function' ? getSelectedSnapshot() : selected),
    [getSelectedSnapshot, selected]
  )

  const categoryLookup = useMemo(() => {
    const lookup = new Map()
    for (const category of fileTree) {
      lookup.set(category.id, category)
    }
    return lookup
  }, [fileTree])

  const getFileTypes = useCallback((category) => {
    return category.file_types || category.fileTypes || {}
  }, [])

  const getGrantedFileIds = useCallback((category) => {
    const grantedIds = []
    const fileTypes = getFileTypes(category)

    for (const fileType of Object.values(fileTypes)) {
      for (const file of fileType.files || []) {
        if (isFileGranted(file)) {
          grantedIds.push(file.id)
        }
      }
    }

    return grantedIds
  }, [getFileTypes])

  const getCategoryCheckboxState = useCallback((categoryId) => {
    const category = categoryLookup.get(categoryId)
    if (!category) return { allSelected: false, partiallySelected: false, hasEditable: false }

    if (pendingSelectAll) {
      return { allSelected: true, partiallySelected: false, hasEditable: true }
    }

    if (category.is_loaded === false) {
      const fileTypes = Object.values(getFileTypes(category))
      const editableCount = fileTypes.reduce((acc, t) => acc + getEditableFilesCount(t), 0)

      if (selectionBaseline === 'none' && !dirtyCategorySet.has(categoryId)) {
        return {
          allSelected: false,
          partiallySelected: false,
          hasEditable: editableCount > 0
        }
      }

      if (selectionBaseline === 'all' && !dirtyCategorySet.has(categoryId)) {
        return {
          allSelected: editableCount > 0,
          partiallySelected: false,
          hasEditable: editableCount > 0
        }
      }

      if (dirtyCategorySet.has(categoryId)) {
        return {
          allSelected: false,
          partiallySelected: false,
          hasEditable: editableCount > 0
        }
      }

      const grantedCount = fileTypes.reduce((acc, t) => acc + Math.min(getGrantedFilesCount(t), getEditableFilesCount(t)), 0)
      const totalGrantedCount = fileTypes.reduce((acc, t) => acc + getGrantedFilesCount(t), 0)
      const hasGrantedButNotEditable = editableCount === 0 && totalGrantedCount > 0

      return {
        allSelected: hasGrantedButNotEditable || (grantedCount === editableCount && editableCount > 0),
        partiallySelected: grantedCount > 0 && grantedCount < editableCount,
        hasEditable: editableCount > 0
      }
    }

    const fileTypes = getFileTypes(category)
    let totalEditable = 0
    let totalAll = 0
    let selectedCount = 0

    for (const fileType of Object.values(fileTypes)) {
      for (const file of fileType.files || []) {
        totalAll++
        if (selectedSet.has(file.id)) selectedCount++
        if (isFileEditable(file)) totalEditable++
      }
    }

    return {
      allSelected: selectedCount === totalAll && totalAll > 0,
      partiallySelected: selectedCount > 0 && selectedCount < totalAll,
      hasEditable: totalEditable > 0,
      hasGrantedButNotEditable: totalEditable === 0 && totalAll > 0 && selectedCount === totalAll
    }
  }, [categoryLookup, selectedSet, getFileTypes, pendingSelectAll, dirtyCategorySet, selectionBaseline])

  const getTypeCheckboxState = useCallback((categoryId, typeId) => {
    const category = categoryLookup.get(categoryId)
    if (!category) return { allSelected: false, partiallySelected: false, hasEditable: false }

    if (pendingSelectAll) {
      return { allSelected: true, partiallySelected: false, hasEditable: true }
    }

    const fileTypes = getFileTypes(category)
    const selectedType = Object.values(fileTypes).find(ft => ft.id === typeId)
    if (!selectedType) return { allSelected: false, partiallySelected: false, hasEditable: false }

    if (selectionBaseline === 'all' && !dirtyCategorySet.has(categoryId)) {
      const editableCount = (selectedType.files || []).reduce((acc, file) => acc + (isFileEditable(file) ? 1 : 0), 0)

      return {
        allSelected: editableCount > 0,
        partiallySelected: false,
        hasEditable: editableCount > 0
      }
    }

    let totalEditable = 0
    let totalAll = 0
    let selectedCount = 0

    for (const file of selectedType.files || []) {
      totalAll++
      if (selectedSet.has(file.id)) selectedCount++
      if (isFileEditable(file)) totalEditable++
    }

    return {
      allSelected: selectedCount === totalAll && totalAll > 0,
      partiallySelected: selectedCount > 0 && selectedCount < totalAll,
      hasEditable: totalEditable > 0,
      hasGrantedButNotEditable: totalEditable === 0 && totalAll > 0 && selectedCount === totalAll
    }
  }, [categoryLookup, selectedSet, getFileTypes, pendingSelectAll, selectionBaseline, dirtyCategorySet])

  const loadCategory = useCallback(async (category) => {
    if (!category || category.is_loaded || !onCategoryExpand) {
      return category
    }

    const loadedCategory = await onCategoryExpand(category.id)
    return loadedCategory || categoryLookup.get(category.id) || category
  }, [categoryLookup, onCategoryExpand])

  const toggleAccordion = useCallback(async (categoryId, e) => {
    if (e) e.stopPropagation()

    const category = categoryLookup.get(categoryId)
    if (!category) return

    const nextIsOpen = !openAccordions[categoryId]
    setOpenAccordions(prev => ({ ...prev, [categoryId]: nextIsOpen }))

    if (nextIsOpen && category.is_loaded === false) {
      await loadCategory(category)
    }
  }, [categoryLookup, loadCategory, openAccordions])

  const handleCategoryCheckboxClick = useCallback((categoryId) => async (e) => {
    if (e) e.stopPropagation()
    const category = categoryLookup.get(categoryId)
    if (!category) return

    const currentCheckboxState = getCategoryCheckboxState(categoryId)
    onSelectionIntent?.(categoryId)
    const wasLoaded = category.is_loaded !== false
    const loadedCategory = await loadCategory(category)
    const fileTypes = getFileTypes(loadedCategory)
    const editableFileIds = []

    for (const fileType of Object.values(fileTypes)) {
      for (const file of fileType.files || []) {
        if (isFileEditable(file)) {
          editableFileIds.push(file.id)
        }
      }
    }

    if (pendingSelectAll) {
      const fullSet = new Set(getLatestSelected())
      editableFileIds.forEach(id => fullSet.delete(id))
      onPendingSelectAllDeactivate?.()
      onSelectionChange(Array.from(fullSet))
      return
    }

    const prevSet = new Set(getLatestSelected())

    if (!wasLoaded && !dirtyCategorySet.has(categoryId)) {
      getGrantedFileIds(loadedCategory).forEach(id => prevSet.add(id))
    }

    const isChecked = currentCheckboxState.allSelected

    if (isChecked) {
      editableFileIds.forEach(id => prevSet.delete(id))
    } else {
      editableFileIds.forEach(id => prevSet.add(id))
    }

    onSelectionChange(Array.from(prevSet))
  }, [categoryLookup, dirtyCategorySet, getCategoryCheckboxState, getFileTypes, getGrantedFileIds, getLatestSelected, loadCategory, onSelectionChange, onSelectionIntent, pendingSelectAll, onPendingSelectAllDeactivate, selectionBaseline, dirtyCategoryIds])

  const handleTypeCheckboxClick = useCallback((categoryId, typeId) => async (e) => {
    if (e) e.stopPropagation()
    const category = categoryLookup.get(categoryId)
    if (!category) return

    const currentCheckboxState = getTypeCheckboxState(categoryId, typeId)
    onSelectionIntent?.(categoryId)
    const wasLoaded = category.is_loaded !== false
    const loadedCategory = await loadCategory(category)
    const fileTypes = getFileTypes(loadedCategory)
    const selectedType = Object.values(fileTypes).find(ft => ft.id === typeId)
    if (!selectedType) return

    const editableFileIds = []
    for (const file of selectedType.files || []) {
      if (isFileEditable(file)) {
        editableFileIds.push(file.id)
      }
    }

    if (pendingSelectAll) {
      const fullSet = new Set(getLatestSelected())
      editableFileIds.forEach(id => fullSet.delete(id))
      onPendingSelectAllDeactivate?.()
      onSelectionChange(Array.from(fullSet))
      return
    }

    const prevSet = new Set(getLatestSelected())
    if (!wasLoaded && !dirtyCategorySet.has(categoryId)) {
      getGrantedFileIds(loadedCategory).forEach(id => prevSet.add(id))
    }

    const isChecked = currentCheckboxState.allSelected

    if (isChecked) {
      editableFileIds.forEach(id => prevSet.delete(id))
    } else {
      editableFileIds.forEach(id => prevSet.add(id))
    }

    onSelectionChange(Array.from(prevSet))
  }, [categoryLookup, dirtyCategorySet, getFileTypes, getGrantedFileIds, getLatestSelected, getTypeCheckboxState, loadCategory, onSelectionChange, onSelectionIntent, pendingSelectAll, onPendingSelectAllDeactivate, selectionBaseline, dirtyCategoryIds])

  const handleFileCheckboxClick = useCallback((categoryId, fileId) => (e) => {
    if (e) e.stopPropagation()
    onSelectionIntent?.(categoryId)

    if (pendingSelectAll) {
      const fullSet = new Set(getLatestSelected())
      fullSet.delete(fileId)
      onPendingSelectAllDeactivate?.()
      onSelectionChange(Array.from(fullSet))
      return
    }

    const prevSet = new Set(getLatestSelected())
    if (prevSet.has(fileId)) {
      prevSet.delete(fileId)
    } else {
      prevSet.add(fileId)
    }
    onSelectionChange(Array.from(prevSet))
  }, [getLatestSelected, onSelectionChange, onSelectionIntent, pendingSelectAll, onPendingSelectAllDeactivate])

  const filteredFilesTree = useMemo(() => {
    if (!searchQuery.trim()) return fileTree

    const lowerQuery = searchQuery.toLowerCase()

    return fileTree.filter(category => {
      const categoryLabel = category.folder?.name
        ? `${category.folder.name} / ${category.name}`.toLowerCase()
        : (category.name || '').toLowerCase()

      if (categoryLabel.includes(lowerQuery)) {
        return true
      }

      if (!category.is_loaded) {
        return false
      }

      return Object.values(getFileTypes(category)).some(type => {
        const typeMatches = (type.name || '').toLowerCase().includes(lowerQuery)
        const fileMatches = (type.files || []).some(file =>
          (file.displayName || file.name || file.title || '').toLowerCase().includes(lowerQuery)
        )

        return typeMatches || fileMatches
      })
    })
  }, [fileTree, searchQuery, getFileTypes])

  const CustomCheckbox = ({ checked, indeterminate, onClick, disabled, checkboxLoading = false }) => {
    if (checkboxLoading) {
      return (
        <SelectionSkeletonWrapper>
          <Skeleton variant='rounded' width={18} height={18} animation='wave' />
        </SelectionSkeletonWrapper>
      )
    }

    const Icon = indeterminate ? IndeterminateCheckBoxIcon : (checked ? CheckBoxIcon : CheckBoxOutlineBlankIcon)
    return (
      <CheckboxWrapper $checked={checked || indeterminate} $disabled={disabled} onClick={!disabled ? onClick : undefined}>
        <Icon fontSize='small' />
      </CheckboxWrapper>
    )
  }

  if (loading) {
    return (
      <Container $maxHeight={maxHeight}>
        <SkeletonSearchWrapper $compact={compact}>
          <Skeleton variant='rounded' height={compact ? 34 : 44} animation='wave' />
        </SkeletonSearchWrapper>
        <ScrollableContent $maxHeight={maxHeight}>
          <SkeletonList>
            {Array.from({ length: compact ? 6 : 5 }).map((_, index) => (
              <SkeletonFolderCard key={index} $compact={compact}>
                <SkeletonFolderRow>
                  <Skeleton variant='rounded' width={18} height={18} animation='wave' />
                  <Skeleton variant='rounded' width={22} height={18} animation='wave' />
                  <Skeleton variant='text' width={`${36 + (index % 3) * 10}%`} height={28} animation='wave' />
                  <Skeleton variant='rounded' width={18} height={18} animation='wave' />
                </SkeletonFolderRow>
              </SkeletonFolderCard>
            ))}
          </SkeletonList>
        </ScrollableContent>
      </Container>
    )
  }

  return (
    <Container $maxHeight={maxHeight}>
      <SearchInputWrapper $compact={compact}>
        <SearchIconWrapper>
          <SearchIcon fontSize='small' />
        </SearchIconWrapper>
        <StyledSearchInput
          ref={searchInputRef}
          type='text'
          placeholder='Search files...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          $compact={compact}
        />
      </SearchInputWrapper>

      <ScrollableContent $maxHeight={maxHeight}>
        {isNotNilOrEmpty(filteredFilesTree) && (!shouldCollapse || Object.keys(openAccordions).length > 0)
          ? filteredFilesTree.map(category => {
            const {
              allSelected: catAllSelected,
              partiallySelected: catPartiallySelected,
              hasEditable: catHasEditable
            } = getCategoryCheckboxState(category.id)
            const isOpen = !shouldCollapse || openAccordions[category.id]
            const isCategoryLoading = loadingCategorySet.has(category.id)

            return (
              <CategoryGroup $compact={compact} key={category.id}>
                <TreeRow $compact={compact} $clickable={false}>
                  <RowLeft>
                    <CustomCheckbox
                      checked={catAllSelected}
                      indeterminate={catPartiallySelected}
                      disabled={!catHasEditable}
                      checkboxLoading={isCategoryLoading}
                      onClick={catHasEditable ? handleCategoryCheckboxClick(category.id) : undefined}
                    />
                    <FolderIconWrapper>
                      <FolderOpenIcon fontSize='small' />
                    </FolderIconWrapper>
                    <CategoryLabel
                      $compact={compact}
                      $expandable={shouldCollapse}
                      onClick={shouldCollapse ? (e) => toggleAccordion(category.id, e) : undefined}
                    >
                      {category.folder?.name
                        ? `${category.folder.name} / ${category.name}`
                        : category.name}
                    </CategoryLabel>
                  </RowLeft>
                  {shouldCollapse && (
                    <ExpandButton onClick={(e) => toggleAccordion(category.id, e)}>
                      <ArrowIcon isOpen={isOpen} />
                    </ExpandButton>
                  )}
                </TreeRow>

                {isOpen && (
                  <SubList>
                    {isCategoryLoading && category.is_loaded === false
                      ? (
                        <CategorySkeletonList>
                          {Array.from({ length: 3 }).map((_, index) => (
                            <SkeletonFileRow key={index} $compact={compact}>
                              <Skeleton variant='rounded' width={18} height={18} animation='wave' />
                              <Skeleton variant='text' width={`${48 + index * 12}%`} height={24} animation='wave' />
                            </SkeletonFileRow>
                          ))}
                        </CategorySkeletonList>
                        )
                      : (
                        Object.values(getFileTypes(category)).map(type => {
                          const {
                            allSelected: typeAllSelected,
                            partiallySelected: typePartiallySelected,
                            hasEditable: typeHasEditable
                          } = getTypeCheckboxState(category.id, type.id)

                          return (
                            <TypeGroup key={type.id}>
                              <TreeRow
                                $isType
                                $compact={compact}
                                onClick={typeHasEditable ? handleTypeCheckboxClick(category.id, type.id) : undefined}
                              >
                                <RowLeft>
                                  <CustomCheckbox
                                    checked={typeAllSelected}
                                    indeterminate={typePartiallySelected}
                                    disabled={!typeHasEditable}
                                    checkboxLoading={isCategoryLoading}
                                    onClick={typeHasEditable ? handleTypeCheckboxClick(category.id, type.id) : undefined}
                                  />
                                  <TypeLabel>{type.name}</TypeLabel>
                                </RowLeft>
                              </TreeRow>

                              <FileList>
                                {type.files
                                  ?.filter(file => (file.displayName || file.name || file.title || '').toLowerCase().includes(searchQuery.toLowerCase()))
                                  .map(file => {
                                    const isEditable = isFileEditable(file)

                                    return (
                                      <TreeRow
                                        key={file.id}
                                        $isFile
                                        $compact={compact}
                                        $disabled={!isEditable}
                                        onClick={isEditable ? handleFileCheckboxClick(category.id, file.id) : undefined}
                                      >
                                        <RowLeft>
                                          <CustomCheckbox
                                            checked={pendingSelectAll ? isEditable : selectedSet.has(file.id)}
                                            disabled={!isEditable}
                                            onClick={isEditable ? handleFileCheckboxClick(category.id, file.id) : undefined}
                                          />
                                          <FileIconWrapper>
                                            <InsertDriveFileOutlinedIcon style={{ fontSize: 16 }} />
                                          </FileIconWrapper>
                                          <FileLabel>{file.displayName || file.name || file.title}</FileLabel>
                                        </RowLeft>
                                      </TreeRow>
                                    )
                                  })}
                              </FileList>
                            </TypeGroup>
                          )
                        })
                        )}
                  </SubList>
                )}
              </CategoryGroup>
            )
          })
          : (
            <EmptyState>
              <FolderOpenIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 12 }} />
              No files found.
            </EmptyState>
            )}
      </ScrollableContent>
    </Container>
  )
}

export default FileTreeSelector

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  ${({ $compact }) => $compact && css`margin-bottom: -4px;`}
`

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: flex;
`

const StyledSearchInput = styled.input`
  width: 100%;
  padding: ${({ $compact }) => $compact ? '7px 10px 7px 32px' : '10px 12px 10px 36px'};
  font-size: ${({ $compact }) => $compact ? '12px' : '14px'};
  border: 1px solid #e2e8f0;
  border-radius: ${({ $compact }) => $compact ? '6px' : '8px'};
  outline: none;
  background: #f8fafc;
  color: #1e293b;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    background: white;
    border-color: ${({ theme }) => theme.colors.primary || '#33becc'};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`

const ScrollableContent = styled.div`
  overflow-y: auto;
  ${({ $maxHeight }) => $maxHeight && css`max-height: ${$maxHeight};`}

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
`

const CategoryGroup = styled.div`
  margin-bottom: ${({ $compact }) => $compact ? '4px' : '8px'};
  border: 1px solid #e2e8f0;
  border-radius: ${({ $compact }) => $compact ? '6px' : '8px'};
  overflow: hidden;
  background: white;
`

const SubList = styled.div`
  border-top: 1px solid #f1f5f9;
  background: #fbfcfd;
`

const TypeGroup = styled.div``

const FileList = styled.div`
  padding-bottom: 4px;
`

const TreeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $compact }) => $compact ? '6px 10px' : '12px 16px'};
  cursor: ${({ $clickable = true }) => $clickable ? 'pointer' : 'default'};
  transition: background 0.15s;
  user-select: none;
  background: white;

  &:hover {
    background: #f8fafc;
  }

  ${({ $isType, $compact }) => $isType && css`
    background: #fbfcfd;
    padding: ${$compact ? '5px 10px 5px 22px' : '10px 16px 10px 28px'};
  `}

  ${({ $isFile, $compact }) => $isFile && css`
    background: #f8fafc;
    padding: ${$compact ? '4px 10px 4px 36px' : '8px 16px 8px 44px'};

    &:hover {
      background: #f1f5f9;
    }
  `}

  ${({ $disabled }) => $disabled && css`
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8fafc;
  `}
`

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $checked, $disabled, theme }) =>
    $disabled ? '#cbd5e1' : ($checked ? (theme.colors.primary || '#33becc') : '#cbd5e1')
  };
  transition: color 0.2s;
  cursor: pointer;

  &:hover {
    color: ${({ $checked, $disabled, theme }) =>
      !$disabled && ($checked ? theme.colors.primary : '#94a3b8')
    };
  }
`

const SelectionSkeletonWrapper = styled.div`
  width: 18px;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CategoryLabel = styled.span`
  font-weight: 600;
  font-size: ${({ $compact }) => $compact ? '12px' : '14px'};
  color: #1e293b;
  cursor: ${({ $expandable }) => $expandable ? 'pointer' : 'default'};
`

const TypeLabel = styled.span`
  font-weight: 500;
  font-size: ${({ $compact }) => $compact ? '12px' : '14px'};
  color: #334155;
`

const FileLabel = styled.span`
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FolderIconWrapper = styled.div`
  color: #64748b;
  display: flex;
`

const FileIconWrapper = styled.div`
  color: #94a3b8;
  display: flex;
`

const ExpandButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  color: #64748b;

  &:hover {
    background: #f1f5f9;
  }
`

const ArrowIcon = styled(KeyboardArrowDownIcon)`
  transition: transform 0.2s ease;
  font-size: 20px !important;

  ${({ isOpen }) => isOpen && 'transform: rotate(180deg);'}
`

const SkeletonSearchWrapper = styled.div`
  ${({ $compact }) => $compact && css`margin-bottom: -4px;`}
`

const SkeletonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkeletonFolderCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: ${({ $compact }) => $compact ? '6px' : '8px'};
  overflow: hidden;
  background: white;
`

const SkeletonFolderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
`

const CategorySkeletonList = styled.div`
  padding: 6px 0;
  background: #f8fafc;
`

const SkeletonFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ $compact }) => $compact ? '6px 10px 6px 36px' : '8px 16px 8px 44px'};
`

const EmptyState = styled.div`
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
`
